import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { G100ConsoleSendRequest } from '@/types'

export function TypedNameDialog({ request, cameraName, sending, onCancel, onConfirm }: {
  request: G100ConsoleSendRequest | null
  cameraName: string
  sending: boolean
  onCancel: () => void
  onConfirm: (name: string) => void
}) {
  const { t } = useTranslation('g100Console')
  const [typed, setTyped] = useState('')
  const open = request !== null
  const close = (next: boolean) => {
    if (!next && !sending) {
      setTyped('')
      onCancel()
    }
  }
  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 space-y-4 rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <Dialog.Title className="text-lg font-semibold">
              {t('confirm.title', { action: request?.action ?? '' })}
            </Dialog.Title>
          </div>
          <Dialog.Description className="text-sm text-muted-foreground">
            {t('confirm.body', { action: request?.action ?? '', camera: cameraName })}
          </Dialog.Description>
          <div className="space-y-1">
            <label htmlFor="g100-console-confirm-name" className="text-xs font-medium">
              {t('confirm.label')} <span className="font-mono">({cameraName})</span>
            </label>
            <input
              id="g100-console-confirm-name"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => close(false)} disabled={sending}>
              {t('confirm.cancel')}
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              disabled={typed !== cameraName || sending}
              onClick={() => onConfirm(typed)}
            >
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('confirm.send')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
